import { useState } from 'react';
import { styled } from '@mui/material/styles';

import { SIDEBAR_GROUND } from '../../components/Frame';
import { notchMask } from '../../components/notch';
import type { Opportunity } from '../../shared/opportunity';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { RsvpStatus } from '../../shared/useRsvp';
import { formatTimeRange } from '../../shared/taxonomy';
import { chipTime, foldCell, inMonth, monthGrid, WEEKDAY_HEADS, type Month } from './month';

/**
 * The Calendar layout's month from 600px up: a sheet of six weeks, each day
 * listing its events as small paper tickets. Going is solid paper and Maybe
 * half strength, as their tickets are; an event on neither list is a plain
 * chip, paper-edged on the ground, so the user's own stand out in Explore's
 * busy month. A chip opens the same callout the list does; the calendar stays
 * where it is underneath.
 */

/** The chip's stub, holding the time; the perforation falls at its edge. */
const STUB_W = 42;
/** Below this a cell is too narrow for a stub: the time runs inline before the name. */
const NARROW_CELL = 118;

const Sheet = styled('div')({
  position: 'relative',
  background: SIDEBAR_GROUND,
  padding: '6px 10px 12px',
  ...notchMask({ radius: 14 }),
});

const Heads = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  padding: '8px 0 6px',
});

const Head = styled('div')({
  padding: '0 8px',
  fontFamily: FONTS.body,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'rgba(243,238,231,0.55)',
});

const Grid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gridAutoRows: 'minmax(112px, auto)',
  borderTop: '1px solid rgba(243,238,231,0.12)',
  borderLeft: '1px solid rgba(243,238,231,0.12)',
  '@media (max-width: 899.95px)': { gridAutoRows: 'minmax(92px, auto)' },
});

const Cell = styled('div', { shouldForwardProp: (p) => p !== '$outside' })<{ $outside: boolean }>(
  ({ $outside }) => ({
    minWidth: 0,
    padding: '6px 6px 8px',
    borderRight: '1px solid rgba(243,238,231,0.12)',
    borderBottom: '1px solid rgba(243,238,231,0.12)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    containerType: 'inline-size',
    background: $outside ? 'rgba(0,0,0,0.14)' : 'transparent',
    '& > *': { opacity: $outside ? 0.45 : 1 },
  }),
);

const DayNumber = styled('div', { shouldForwardProp: (p) => p !== '$today' && p !== '$past' })<{
  $today: boolean;
  $past: boolean;
}>(({ $today, $past }) => ({
  alignSelf: 'flex-start',
  minWidth: 24,
  height: 24,
  padding: '0 6px',
  marginBottom: 2,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: $today ? BRAND.mocha : BRAND.parchment,
  background: $today ? BRAND.parchment : 'transparent',
  opacity: $past && !$today ? 0.55 : 1,
}));

/** Keeps a focus or open ring outside the chip's mask, which would clip it. */
const ChipRing = styled('div', { shouldForwardProp: (p) => p !== '$active' })<{ $active: boolean }>(
  ({ $active }) => ({
    borderRadius: 4,
    outline: $active ? `1.5px solid ${BRAND.parchment}` : 'none',
    outlineOffset: 2,
    '&:has(:focus-visible)': { outline: `2px solid ${BRAND.parchment}` },
  }),
);

/** Going, Maybe, or on neither list. */
type ChipTone = RsvpStatus | 'plain';

const Chip = styled('button', { shouldForwardProp: (p) => p !== '$tone' && p !== '$cancelled' })<{
  $tone: ChipTone;
  $cancelled: boolean;
}>(({ $tone, $cancelled }) => ({
  appearance: 'none',
  border: 0,
  outline: 'none',
  width: '100%',
  height: 23,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  background: $tone === 'plain' ? 'rgba(243,238,231,0.13)' : BRAND.parchment,
  color: $tone === 'plain' ? BRAND.parchment : BRAND.mocha,
  opacity: $tone === 'maybe' ? 0.5 : 1,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: FONTS.body,
  transition: 'transform 120ms ease',
  ...notchMask({ radius: 3.5, perforationAt: `${STUB_W}px` }),
  '&:hover': {
    transform: 'translateY(-1px)',
    ...($tone === 'plain' ? { background: 'rgba(243,238,231,0.22)' } : null),
  },
  '& .chip-time': {
    flex: `0 0 ${STUB_W}px`,
    textAlign: 'center',
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    borderRight: `1px dashed ${$tone === 'plain' ? 'rgba(243,238,231,0.3)' : 'rgba(54,47,42,0.35)'}`,
    lineHeight: '15px',
  },
  '& .chip-name': {
    flex: 1,
    minWidth: 0,
    padding: '0 6px 0 6px',
    fontSize: 11.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textDecoration: $cancelled ? 'line-through' : 'none',
  },
  [`@container (max-width: ${NARROW_CELL}px)`]: {
    ...notchMask({ radius: 3 }),
    '& .chip-time': { flex: '0 0 auto', padding: '0 0 0 6px', border: 0, textAlign: 'left' },
    '& .chip-name': { paddingLeft: 4 },
  },
}));

const More = styled('button')({
  appearance: 'none',
  border: 0,
  background: 'transparent',
  padding: '1px 2px',
  alignSelf: 'flex-start',
  color: 'rgba(243,238,231,0.75)',
  fontFamily: FONTS.body,
  fontSize: 11.5,
  fontWeight: 700,
  cursor: 'pointer',
  '&:hover': { color: BRAND.parchment, textDecoration: 'underline', textUnderlineOffset: 3 },
});

interface Props {
  month: Month;
  today: string;
  days: ReadonlyMap<string, Opportunity[]>;
  statusOf: (id: string) => RsvpStatus | null;
  /** The event whose callout is open, ringed so it can be found again. */
  activeId: string | null;
  onOpen: (event: Opportunity) => void;
}

export default function MonthCalendar({ month, today, days, statusOf, activeId, onOpen }: Props) {
  /** Days opened past three chips. The view is keyed by month, so this starts empty on each. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (day: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (!next.delete(day)) next.add(day);
      return next;
    });

  return (
    <Sheet>
      <Heads aria-hidden="true">
        {WEEKDAY_HEADS.map((d) => (
          <Head key={d}>{d}</Head>
        ))}
      </Heads>
      <Grid>
        {monthGrid(month.year, month.month).flat().map((day) => {
          const events = days.get(day) ?? [];
          const open = expanded.has(day);
          const { shown, more } = foldCell(events, open);
          const date = Number(day.slice(8));
          return (
            <Cell key={day} $outside={!inMonth(day, month)} data-day={day}>
              <DayNumber $today={day === today} $past={day < today} aria-label={day === today ? `${date}, today` : undefined}>
                {date}
              </DayNumber>
              {shown.map((event) => {
                const status = statusOf(event.id);
                const cancelled = event.status === 'cancelled';
                return (
                  <ChipRing key={event.id} $active={event.id === activeId}>
                    <Chip
                      type="button"
                      $tone={status ?? 'plain'}
                      $cancelled={cancelled}
                      title={`${event.name} · ${formatTimeRange(event)}${cancelled ? ' · Cancelled' : ''}`}
                      aria-label={`${event.name}, ${formatTimeRange(event)}${status ? `, ${status === 'maybe' ? 'Maybe' : 'Going'}` : ''}${cancelled ? ', cancelled' : ''}`}
                      onClick={() => onOpen(event)}
                    >
                      <span className="chip-time">{chipTime(event)}</span>
                      <span className="chip-name">{event.name}</span>
                    </Chip>
                  </ChipRing>
                );
              })}
              {more > 0 && (
                <More type="button" aria-expanded={false} onClick={() => toggle(day)}>
                  +{more} more
                </More>
              )}
              {open && events.length > 3 && (
                <More type="button" aria-expanded onClick={() => toggle(day)}>
                  Show less
                </More>
              )}
            </Cell>
          );
        })}
      </Grid>
    </Sheet>
  );
}
